import {
  Row,
  Col,
  Card,
  Table,
  Button,
  Tag,
  Tooltip,
  Input,
  Select,
} from "antd";
import React, { useState, useEffect } from "react";
import useRequest from "../../hooks/useRequest";
import { ShowToast, Severty } from "../../helper/toast";
import ShowTotal from "../../components/ShowTotal";
import apiPath from "../../constants/apiPath";
import useDebounce from "../../hooks/useDebounce";
import { ExclamationCircleOutlined, UndoOutlined } from "@ant-design/icons";
import FilterBar, { FilterBarFilters } from "../../components/FilterBar";
import confirm from "antd/lib/modal/confirm";
import PolicyForm from "./AddForm";
import moment from "moment";

const { Search } = Input;
const { Option } = Select;

function Index() {
  const { request } = useRequest();

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [visible, setVisible] = useState(false);
  const [selected, setSelected] = useState(null);
  const [isUpdate, setIsUpdate] = useState(false);

  const [searchText, setSearchText] = useState("");
  const debouncedSearchText = useDebounce(searchText, 300);

  const [filters, setFilters] = useState({
    status: undefined,
    type: undefined,
  });
  const [showFilters, setShowFilters] = useState(false);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  // ---------- FETCH POLICIES ----------
  const fetchData = () => {
    setLoading(true);

    const params = new URLSearchParams();

    if (debouncedSearchText) {
      params.append("search", debouncedSearchText);
    }

    if (filters.status !== undefined) {
      params.append("status", filters.status);
    }

    if (filters.type) {
      params.append("type", filters.type);
    }

    request({
      url: `${apiPath.adminListPolicies}?${params.toString()}`,
      method: "GET",
      onSuccess: ({ data }) => {
        setLoading(false);
        setList(data?.data || []);
        setPagination((prev) => ({
          ...prev,
          total: data?.total || 0,
        }));
      },
      onError: (error) => {
        setLoading(false);
        ShowToast(error, Severty.ERROR);
      },
    });
  };

  // ---------- RESET ----------
  const resetData = () => {
    setSearchText("");
    setFilters({ status: undefined, type: undefined });
    setRefresh((prev) => !prev);
  };

  // ---------- PUBLISH POLICY ----------
  const confirmPublish = (record) => {
    confirm({
      icon: <ExclamationCircleOutlined />,
      content: (
        <p style={{ fontSize: 18, fontWeight: "bold", textAlign: "center" }}>
          Publish this policy version?
        </p>
      ),
      onOk() {
        request({
          url: `${apiPath.updatePolicy}/${record._id}`,
          method: "PUT",
          data: { publish: "PUBLISH" },
          onSuccess: (res) => {
            ShowToast(res.message, Severty.SUCCESS);
            fetchData();
          },
          onError: (error) => {
            ShowToast(error, Severty.ERROR);
          },
        });
      },
    });
  };

  // ---------- TABLE COLUMNS ----------
  const columns = [
    {
      title: "Policy Type",
      dataIndex: "type",
      render: (type) => (
        <Tag color={type === "TERMS" ? "blue" : "purple"}>
          {type === "TERMS" ? "Terms & Conditions" : "Privacy Policy"}
        </Tag>
      ),
    },
    {
      title: "Version",
      dataIndex: "version",
    },
    // {
    //   title: "Change Type",
    //   dataIndex: "changeType",
    //   render: (val) => (
    //     <Tag color={val === "MAJOR" ? "red" : "green"}>{val}</Tag>
    //   ),
    // },
    {
      title: "Status",
      dataIndex: "status",
      render: (status) => (
        <Tag color={status === "PUBLISH" ? "green" : "orange"}>{status}</Tag>
      ),
    },
    {
      title: "Created On",
      dataIndex: "createdAt",
      render: (date) => moment(date).format("DD-MM-YYYY"),
    },
    {
      title: "Action",

      render: (_, record) => (
        <>
          <Tooltip title="Edit Policy">
            <Button
              disabled={record.isActive && record.changeType === "MAJOR"}
              onClick={() => {
                setSelected(record);
                setIsUpdate(true);
                setVisible(true);
              }}
            >
              <i className="fa fa-pen" />
            </Button>
          </Tooltip>

          {record.isActive && record.status === "DRAFT" && (
            <Tooltip title="Publish Policy">
              <Button onClick={() => confirmPublish(record)}>
                <i className="fa fa-upload" />
              </Button>
            </Tooltip>
          )}
        </>
      ),
    },
  ];

  // ---------- EFFECT ----------
  useEffect(() => {
    fetchData();
  }, [filters, refresh, debouncedSearchText]);

  return (
    <>
      <div className="tabled">
        <Row gutter={[24, 0]}>
          <Col span={24}>
            <Card
              bordered={false}
              className="criclebox tablespace mb-24"
              title="App Policies Management"
              extra={
                <FilterBar
                  showFilters={showFilters}
                  onToggle={() => setShowFilters((v) => !v)}
                  rightContent={
                    <Button
                      type="primary"
                      onClick={() => {
                        setSelected(null);
                        setIsUpdate(false);
                        setVisible(true);
                      }}
                    >
                      Add Policy
                    </Button>
                  }
                />
              }
            >
              {showFilters && (
                <FilterBarFilters>
                  <Select
                    placeholder="Policy Type"
                    allowClear
                    value={filters.type}
                    onChange={(value) =>
                      setFilters({ ...filters, type: value })
                    }
                  >
                    <Option value="TERMS">Terms & Conditions</Option>
                    <Option value="PRIVACY">Privacy Policy</Option>
                  </Select>
                  <Select
                    placeholder="Status"
                    allowClear
                    value={filters.status}
                    onChange={(value) =>
                      setFilters({ ...filters, status: value })
                    }
                  >
                    <Option value="PUBLISH">Published</Option>
                    <Option value="DRAFT">Draft</Option>
                  </Select>
                  <Button icon={<UndoOutlined />} onClick={resetData}>
                    Reset
                  </Button>
                </FilterBarFilters>
              )}
              <div className="text-end mb-2">
                {ShowTotal(pagination.total || 0)}
              </div>

              <Table
                loading={loading}
                columns={columns}
                dataSource={list}
                rowKey="_id"
                pagination={{
                  current: pagination.current,
                  defaultPageSize: 10,
                  responsive: true,
                  total: pagination.total,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  pageSizeOptions: ["10", "20", "30", "50"],
                }}
              />
            </Card>
          </Col>
        </Row>
      </div>

      {visible && (
        <PolicyForm
          show={visible}
          hide={() => setVisible(false)}
          data={selected}
          refresh={() => setRefresh((prev) => !prev)}
          isUpdate={isUpdate}
        />
      )}
    </>
  );
}

export default Index;
